import { istek } from './istemci';
import type { Semalar } from './sema';

/**
 * YÖNETİM UÇLARI (8) — SEÇENEK ALANI VE TAKVİM.
 *
 * YETKİ BEŞ SEVİYEDE DAĞILIYOR; ekran her uç için ayrı değişken tutar:
 *
 *   GET  /takvim              PERSONEL_GORUNTULE | PARAMETRE_YONET | SISTEM_YONET
 *   GET  /takvim/ufuk         PARAMETRE_YONET | SISTEM_YONET | DONEM_KAPAT
 *   GET  /ayar/../gecmis      PARAMETRE_YONET | SISTEM_YONET | RAPOR_GORUNTULE
 *   GET  /ayar                PARAMETRE_YONET | SISTEM_YONET
 *   POST /ayar/on-izleme      PARAMETRE_YONET | SISTEM_YONET
 *   POST /ayar                PARAMETRE_YONET                     ← YAZMA
 *   POST /takvim              PARAMETRE_YONET                     ← YAZMA
 *   DEL  /takvim/:tarih       PARAMETRE_YONET                     ← YAZMA
 *
 * YAZMA OKUMADAN DAR. `SISTEM_YONET` sahibi ayarları görür ama DEĞİŞTİREMEZ;
 * düğme ona gösterilseydi 403 alırdı.
 *
 * AYAR UPDATE EDİLMEZ, SÜRÜMLENİR: eski satır kapatılır, yeni satır açılır.
 * Geçmiş puantajın hangi katsayıyla hesaplandığı geriye dönük okunabilmeli.
 */

export type AyarSatiri = Semalar['AyarSatiriDto'];
export type AyarGecmisSatiri = Semalar['AyarGecmisSatiriDto'];
export type DegisiklikOnIzlemesi = Semalar['DegisiklikOnIzlemeDto'];
export type DegisiklikSonucu = Semalar['DegisiklikSonucuDto'];
export type TakvimGunu = Semalar['TakvimGunuDto'];
export type TakvimUfku = Semalar['TakvimUfkuDto'];
export type TakvimYazmaSonucu = Semalar['TakvimYazmaSonucuDto'];

export type AyarKaynagi = AyarSatiri['kaynak'];
export type TakvimUfukDurumu = TakvimUfku['durum'];

/**
 * DAYANAK TİPLERİ — `oneri_varsayilan` BİLEREK YOK.
 *
 * Backend DTO'sunda da yok. Seçilebilseydi yönetici doğrulanmış bir
 * parametreyi öneriye geri çevirip dönem kilidini farkında olmadan
 * kilitleyebilirdi; `oneri_varsayilan` yalnız kurulum seed'inden gelir.
 *
 * Şemadan türetilemiyor: istek DTO'ları `components['schemas']` altına
 * girmiyor, yalnız yanıt tipleri giriyor.
 */
export const DAYANAK_TIPLERI = [
  'kanun',
  'yonetmelik',
  'yargitay_ictihadi',
  'tis',
  'meclis_karari',
  'baskanlik_onayi',
  'ik_karari',
  'mudur_onayi',
  'hukuk_musavirligi_gorusu',
] as const;

export type DayanakTipi = (typeof DAYANAK_TIPLERI)[number];

/**
 * GÜN TİPİ KATALOĞU — SABİT, çünkü bunu veren UÇ YOK.
 *
 * `POST /yonetim/takvim` `gunTipiId` istiyor ama katalog ucu bulunmuyor
 * (`IS_LISTESI.md` §3'e borç yazıldı). Sabit yazmanın riski sınırlı:
 * `takvim` tablosundaki CHECK kısıtı `gun_tipi_id = 30` değerine DOĞRUDAN
 * bağlı, yani id'ler şema düzeyinde dondurulmuş.
 *
 * Takvimde KULLANILMAYANLAR listede yok: `NORMAL` (0) kaydın YOKLUĞU
 * demektir, `HAFTA_TATILI` (20) kişiye özeldir ve `vardiya_plan`dan gelir.
 */
export const YARIM_GUN_TIPI_ID = 30;

export const GUN_TIPLERI = [
  { id: YARIM_GUN_TIPI_ID, kod: 'YARIM_GUN_TATIL', ad: 'Yarım gün tatil' },
  { id: 35, kod: 'ULUSAL_BAYRAM', ad: 'Ulusal bayram' },
  { id: 36, kod: 'GENEL_TATIL', ad: 'Genel tatil' },
  { id: 40, kod: 'DINI_BAYRAM', ad: 'Dini bayram' },
  { id: 10, kod: 'IDARI_TATIL', ad: 'İdari izin' },
] as const;

/* --- İstemci tarafı istek şekilleri — DTO karşılığı değildir --------- */

export interface OnIzlemeGirdisi {
  kaynak: AyarKaynagi;
  kod: string;
  gecerlilikBaslangic: string;
}

export interface AyarDegistirGirdisi extends OnIzlemeGirdisi {
  yeniDeger: string;
  dayanakTipi: DayanakTipi;
  dayanakNo: string;
  dayanakTarihi: string;
  gerekce: string;
  aciklama?: string;
}

export interface TakvimGunGirdisi {
  tarih: string;
  gunTipiId: number;
  ad: string;
  /** `gunTipiId === 30` ise ZORUNLU, değilse GÖNDERİLMEZ (DB CHECK kısıtı). */
  yarimGunBaslangic?: string;
  dayanakTipi: string;
  dayanakMadde?: string;
  aciklama?: string;
  gerekce: string;
}

/* ------------------------------------------------------------------ */

/**
 * Değiştirilebilir ayarların tamamı — üç kaynak tek listede.
 *
 * `yalnizDogrulanmamis` YALNIZ `true` İKEN GÖNDERİLİR. Sunucu DTO'su
 * `@IsBoolean()` ve `enableImplicitConversion` açık; bu bileşim projede bir
 * kez `COOKIE_SECURE=false` değerini `true` okumuştu. `istek()` `undefined`
 * sorguları zaten düşürüyor, `false` hiç yola çıkmıyor.
 */
export function ayarListesi(yalnizDogrulanmamis = false): Promise<AyarSatiri[]> {
  return istek<AyarSatiri[]>('/v1/yonetim/ayar', {
    sorgu: { yalnizDogrulanmamis: yalnizDogrulanmamis ? true : undefined },
  });
}

export function ayarGecmisi(kaynak: AyarKaynagi, kod: string): Promise<AyarGecmisSatiri[]> {
  return istek<AyarGecmisSatiri[]>(`/v1/yonetim/ayar/${kaynak}/${encodeURIComponent(kod)}/gecmis`);
}

/**
 * Değişiklik ön izlemesi — KAYDETMEZ.
 *
 * Panel bunu "Kaydet"ten ÖNCE çağırır ve sonucu görmeden kayda izin vermez:
 * 40 kişinin iki aylık puantajını değiştiren bir işlem, tek tıkla yapılan bir
 * işlem olmamalı.
 */
export function ayarOnIzleme(girdi: OnIzlemeGirdisi): Promise<DegisiklikOnIzlemesi> {
  return istek<DegisiklikOnIzlemesi>('/v1/yonetim/ayar/on-izleme', {
    yontem: 'POST',
    govde: girdi,
  });
}

export function ayarDegistir(girdi: AyarDegistirGirdisi): Promise<DegisiklikSonucu> {
  return istek<DegisiklikSonucu>('/v1/yonetim/ayar', { yontem: 'POST', govde: girdi });
}

/* --- Takvim --------------------------------------------------------- */

export function takvimUfku(): Promise<TakvimUfku> {
  return istek<TakvimUfku>('/v1/yonetim/takvim/ufuk');
}

export function takvimListesi(baslangic: string, bitis: string): Promise<TakvimGunu[]> {
  return istek<TakvimGunu[]>('/v1/yonetim/takvim', { sorgu: { baslangic, bitis } });
}

export function takvimGunYaz(girdi: TakvimGunGirdisi): Promise<TakvimYazmaSonucu> {
  return istek<TakvimYazmaSonucu>('/v1/yonetim/takvim', { yontem: 'POST', govde: girdi });
}

/**
 * Takvim gününü siler — sistemin "silme yok" kuralının TEK İSTİSNASI.
 *
 * Kaydın VARLIĞI tatil olduğu anlamına gelir; pasife alma diye bir seçenek
 * yok. Gerekçe gövdede taşınır (DELETE + gövde bilinçli tercih: yol
 * parametresine gerekçe sığmaz ve sunucu denetim izine yazıyor).
 */
export function takvimGunSil(tarih: string, gerekce: string): Promise<void> {
  return istek<void>(`/v1/yonetim/takvim/${tarih}`, {
    yontem: 'DELETE',
    govde: { gerekce },
  });
}
